import { z } from "zod";

import { jobControlStateSchema } from "./cloud-preserve";
import { restoreJobStatusSchema, restoreStepTypeSchema } from "./restore-plan";
import { snapshotComparisonSchema } from "./snapshot-compare";

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const trelloIdentitySchema = z.object({
  boardId: z.string().min(1).max(512),
  workspaceId: z.string().min(1).max(512),
});

export const inPlaceRestorePreviewRequestSchema = z.object({
  targetSnapshotId: z.uuid(),
});
export type InPlaceRestorePreviewRequest = z.infer<
  typeof inPlaceRestorePreviewRequestSchema
>;

export const inPlaceRestorePolicySchema = z.object({
  archivesExtraItems: z.literal(false),
  createsMissingItems: z.literal(true),
  exactBoardIdentityRequired: z.literal(true),
  exactWorkspaceIdentityRequired: z.literal(true),
  freshSafetyBackupRequired: z.literal(true),
  leavesExtraItemsIntact: z.literal(true),
  updatesSupportedValues: z.literal(true),
  usesDeleteEndpoints: z.literal(false),
});
export type InPlaceRestorePolicy = z.infer<typeof inPlaceRestorePolicySchema>;

export const inPlaceRestorePreviewSchema = z
  .object({
    comparison: snapshotComparisonSchema,
    comparisonDirection: z.literal("target_to_live"),
    destination: trelloIdentitySchema,
    expiresAt: z.iso.datetime(),
    generatedAt: z.iso.datetime(),
    liveComparisonSnapshotId: z.uuid(),
    policy: inPlaceRestorePolicySchema,
    previewSha256: sha256Schema,
    safety: z.object({
      dryRun: z.literal(true),
      requiresExplicitConfirmation: z.literal(true),
      trelloWrites: z.literal(0),
    }),
    schemaVersion: z.literal(1),
    source: trelloIdentitySchema.extend({
      capturedAt: z.iso.datetime(),
    }),
    targetSnapshotId: z.uuid(),
  })
  .superRefine((value, context) => {
    if (value.expiresAt <= value.generatedAt) {
      context.addIssue({
        code: "custom",
        message: "The preview must expire after it is generated.",
        path: ["expiresAt"],
      });
    }
    if (
      value.source.boardId !== value.destination.boardId ||
      value.source.workspaceId !== value.destination.workspaceId
    ) {
      context.addIssue({
        code: "custom",
        message: "Source and destination identities must match exactly.",
        path: ["destination"],
      });
    }
    if (
      value.comparison.from.snapshotId !== value.targetSnapshotId ||
      value.comparison.to.snapshotId !== value.liveComparisonSnapshotId
    ) {
      context.addIssue({
        code: "custom",
        message:
          "The comparison must be bound from the target backup to the live board.",
        path: ["comparison"],
      });
    }
  });
export type InPlaceRestorePreview = z.infer<typeof inPlaceRestorePreviewSchema>;

export const inPlaceRestorePreparationRequestSchema = z.object({
  createFreshSafetyBackup: z.boolean().default(true),
  idempotencyKey: z.uuid(),
  previewSha256: sha256Schema,
  targetSnapshotId: z.uuid(),
});
export type InPlaceRestorePreparationRequest = z.infer<
  typeof inPlaceRestorePreparationRequestSchema
>;

export const inPlaceRestorePreparationStatusSchema = z.enum([
  "safety_backup_pending",
  "safety_backup_running",
  "ready_for_confirmation",
  "confirmed",
  "failed",
  "expired",
]);
export type InPlaceRestorePreparationStatus = z.infer<
  typeof inPlaceRestorePreparationStatusSchema
>;

export const inPlaceRestorePreparationResponseSchema = z
  .object({
    expiresAt: z.iso.datetime(),
    operationCount: z.int().nonnegative(),
    planSha256: sha256Schema.nullable(),
    preparationId: z.uuid(),
    safetyBackup: z.object({
      captureJobId: z.uuid(),
      mode: z.enum(["fresh", "reused"]).default("fresh"),
      snapshotId: z.uuid().nullable(),
    }),
    status: inPlaceRestorePreparationStatusSchema,
    targetSnapshotId: z.uuid(),
  })
  .superRefine((value, context) => {
    const ready = ["ready_for_confirmation", "confirmed"].includes(
      value.status,
    );
    if (
      ready &&
      (value.planSha256 === null ||
        value.safetyBackup.snapshotId === null ||
        value.operationCount === 0)
    ) {
      context.addIssue({
        code: "custom",
        message:
          "A confirmation-ready preparation requires both a plan hash and a completed safety backup.",
        path: ["status"],
      });
    }
    if (
      ["safety_backup_pending", "safety_backup_running"].includes(
        value.status,
      ) &&
      (value.planSha256 !== null ||
        value.safetyBackup.snapshotId !== null ||
        value.operationCount !== 0)
    ) {
      context.addIssue({
        code: "custom",
        message:
          "A pending safety backup cannot expose a plan or confirmation operations.",
        path: ["status"],
      });
    }
  });
export type InPlaceRestorePreparationResponse = z.infer<
  typeof inPlaceRestorePreparationResponseSchema
>;

export const activeInPlaceRestorePreparationResponseSchema = z
  .object({
    preparation: inPlaceRestorePreparationResponseSchema.nullable(),
  })
  .strict();
export type ActiveInPlaceRestorePreparationResponse = z.infer<
  typeof activeInPlaceRestorePreparationResponseSchema
>;

export const inPlaceRestoreConfirmationRequestSchema = z.object({
  acknowledgements: z.object({
    changesOriginalBoard: z.literal(true),
    comparisonReviewed: z.literal(true),
    extrasRemainIntact: z.literal(true),
    fidelityReviewed: z.literal(true),
    noDeleteEndpoints: z.literal(true),
    safetyBackupCompleted: z.literal(true),
  }),
  idempotencyKey: z.uuid(),
  planSha256: sha256Schema,
  preparationId: z.uuid(),
  targetSnapshotId: z.uuid(),
});
export type InPlaceRestoreConfirmationRequest = z.infer<
  typeof inPlaceRestoreConfirmationRequestSchema
>;

export const inPlaceRestoreConfirmationResponseSchema = z.object({
  dispatchStatus: z.enum(["disabled", "queued"]),
  jobId: z.uuid(),
  operationCount: z.int().positive(),
  planSha256: sha256Schema,
  preparationId: z.uuid(),
  status: z.enum(["prepared", "queued"]),
});
export type InPlaceRestoreConfirmationResponse = z.infer<
  typeof inPlaceRestoreConfirmationResponseSchema
>;

export const IN_PLACE_RESTORE_REPORT_VISIBILITY_DAYS = 30;

export const inPlaceRestoreOperationStatusSchema = z.enum([
  "pending",
  "running",
  "applied",
  "unchanged",
  "skipped",
  "failed",
]);
export type InPlaceRestoreOperationStatus = z.infer<
  typeof inPlaceRestoreOperationStatusSchema
>;

export const inPlaceRestoreOutcomeCountsSchema = z.object({
  applied: z.int().nonnegative(),
  failed: z.int().nonnegative(),
  pending: z.int().nonnegative(),
  running: z.int().nonnegative(),
  skipped: z.int().nonnegative(),
  unchanged: z.int().nonnegative(),
});
export type InPlaceRestoreOutcomeCounts = z.infer<
  typeof inPlaceRestoreOutcomeCountsSchema
>;

const inPlaceRestoreResultCodeSchema = z.string().regex(/^[a-z0-9_]{1,80}$/);

export const inPlaceRestoreSummarySchema = z
  .object({
    completedAt: z.iso.datetime().nullable(),
    controlState: jobControlStateSchema,
    createdAt: z.iso.datetime(),
    destinationBoardId: z.string().min(1).max(512),
    errorCode: inPlaceRestoreResultCodeSchema.nullable(),
    jobId: z.uuid(),
    outcomes: inPlaceRestoreOutcomeCountsSchema,
    preparationId: z.uuid(),
    progress: z.object({
      completed: z.int().nonnegative(),
      total: z.int().nonnegative(),
    }),
    safetySnapshotId: z.uuid(),
    startedAt: z.iso.datetime().nullable(),
    status: restoreJobStatusSchema,
    targetSnapshotId: z.uuid(),
  })
  .strict();
export type InPlaceRestoreSummary = z.infer<typeof inPlaceRestoreSummarySchema>;

export const inPlaceRestoreHistorySchema = z
  .object({
    restores: z.array(inPlaceRestoreSummarySchema).max(20),
  })
  .strict();
export type InPlaceRestoreHistory = z.infer<typeof inPlaceRestoreHistorySchema>;

export const inPlaceRestoreOperationReportSchema = z
  .object({
    action: z.enum(["create", "update", "leave_unchanged", "skip"]),
    attemptCount: z.int().min(0).max(10),
    completedAt: z.iso.datetime().nullable(),
    destinationId: z.string().min(1).max(512).nullable(),
    operationType: restoreStepTypeSchema,
    resultCode: inPlaceRestoreResultCodeSchema.nullable(),
    sequence: z.int().positive(),
    sourceId: z.string().min(1).max(512),
    status: inPlaceRestoreOperationStatusSchema,
  })
  .strict();
export type InPlaceRestoreOperationReport = z.infer<
  typeof inPlaceRestoreOperationReportSchema
>;

export const inPlaceRestoreReportSchema = inPlaceRestoreSummarySchema.extend({
  nextAfter: z.int().positive().nullable(),
  operations: z.array(inPlaceRestoreOperationReportSchema).max(100),
});
export type InPlaceRestoreReport = z.infer<typeof inPlaceRestoreReportSchema>;

export const inPlaceRestoreControlResponseSchema = z
  .object({
    controlState: jobControlStateSchema,
    executionDispatch: z.enum(["disabled", "not_dispatched", "queued"]),
    jobId: z.uuid(),
    status: restoreJobStatusSchema,
    updatedAt: z.iso.datetime(),
  })
  .strict();
export type InPlaceRestoreControlResponse = z.infer<
  typeof inPlaceRestoreControlResponseSchema
>;
